/**
 * TTS Timing Utilities
 * 
 * Provides timing mechanisms for TTS:
 * 1. Measured timing using word boundary events from the TTS plugin
 * 2. Estimated timing fallback using WPM calculation
 */

import { TextToSpeech } from '@capacitor-community/text-to-speech';

/**
 * Subscribe to word boundary events during speech
 * @param {Function} callback - Called with {start, end, spokenWord} for each word
 * @returns {Object} Listener object with remove() method
 */
export function onWord(callback) {
  // Check if we're in a Capacitor environment
  if (typeof window !== 'undefined' && window.Capacitor) {
    try {
      return TextToSpeech.addListener('onRangeStart', callback);
    } catch (error) {
      console.error('TTS onWord listener error:', error);
      return createDummyListener();
    }
  }
  
  // Web fallback - try to use speechSynthesis events if available
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    return createWebSpeechListener(callback);
  }
  
  // Final fallback - return dummy listener
  return createDummyListener();
}

/**
 * Create a dummy listener for environments without TTS support
 * @returns {Object} Dummy listener with proper async remove method
 */
function createDummyListener() {
  return {
    remove: () => Promise.resolve()
  };
}

/**
 * Create a web speech listener that attempts to provide word boundary events
 * @param {Function} callback - Called with {start, end, spokenWord} for each word
 * @returns {Object} Listener object with remove() method
 */
function createWebSpeechListener(callback) {
  let currentUtterance = null;
  let wordIndex = 0;
  
  // Listen for when speech starts to hook into the current utterance
  const handleSpeechStart = () => {
    // Reset word tracking
    wordIndex = 0;
  };
  
  // Listen for speech end to simulate final word completion
  const handleSpeechEnd = () => {
    // Simulate final word completion event
    if (currentUtterance && currentUtterance.text) {
      callback({
        start: 0,
        end: currentUtterance.text.length,
        spokenWord: currentUtterance.text
      });
    }
  };
  
  // Store references to clean up later
  const cleanup = () => {
    // Remove any event listeners if they were added
    // Note: Web Speech API doesn't provide word-level events,
    // so we can't provide the same granularity as native TTS
  };
  
  return {
    remove: () => {
      cleanup();
      return Promise.resolve();
    }
  };
}

/**
 * Estimate reading duration in milliseconds
 * @param {string} text - Text to estimate duration for
 * @param {number} rate - Speech rate multiplier (default: 1.0)
 * @returns {number} Estimated duration in milliseconds
 */
export function estimateReadMs(text, rate = 1.0) {
  if (!text || !text.trim()) return 500; // Minimum 500ms for empty text
  
  // Extract words (including contractions and hyphenated words)
  const words = (text.match(/\b[\w''-]+\b/g) || []).length || Math.ceil(text.length / 5);
  
  // Base speaking rate: ~170 words per minute at rate 1.0
  // Adjust for speech rate
  const baseWPM = 170 * rate;
  
  // Calculate base duration from word count
  const baseMs = (words / baseWPM) * 60000;
  
  // Add pauses for punctuation
  const fullStops = (text.match(/[.!?]/g) || []).length;
  const minorPauses = (text.match(/[,;:]/g) || []).length;
  const dashPauses = (text.match(/[-–—]/g) || []).length;
  
  // Pause durations (adjusted by rate)
  const fullStopPause = 200 / rate;  // Period, exclamation, question
  const minorPauseDuration = 120 / rate;  // Comma, semicolon, colon
  const dashPauseDuration = 80 / rate;  // Dash, en-dash, em-dash
  
  const punctuationPauses = 
    (fullStops * fullStopPause) + 
    (minorPauses * minorPauseDuration) + 
    (dashPauses * dashPauseDuration);
  
  // Total duration with minimum of 500ms
  return Math.max(500, Math.round(baseMs + punctuationPauses));
}

/**
 * Create a promise that resolves when TTS completes
 * Uses word boundary events with fallback timeout
 * @param {string} text - Text being spoken
 * @param {number} rate - Speech rate
 * @returns {Promise} Resolves when speech completes
 */
export function createTTSCompletionPromise(text, rate = 1.0) {
  return new Promise((resolve) => {
    if (!text || !text.trim()) {
      resolve();
      return;
    }
    
    let listener = null;
    let fallbackTimer = null;
    let completed = false;
    
    const cleanup = async () => {
      if (completed) return;
      completed = true;
      
      if (listener && typeof listener.remove === 'function') {
        try {
          await listener.remove();
        } catch (error) {
          console.error('Error removing TTS listener in cleanup:', error);
        }
        listener = null;
      }
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      
      resolve();
    };
    
    // Subscribe to word events
    listener = onWord(({ end }) => {
      // Check if we've reached the end of the text
      if (end >= text.length) {
        cleanup().catch(console.error);
      }
    });
    
    // Set fallback timeout (add 250ms buffer to estimated time)
    const fallbackMs = estimateReadMs(text, rate) + 250;
    fallbackTimer = setTimeout(() => {
      cleanup().catch(console.error);
    }, fallbackMs);
  });
}

/**
 * Calculate adaptive display time for chess move annotations
 * This maintains compatibility with the existing timing system
 * but can be enhanced with TTS duration
 * @param {string} content - Annotation content
 * @param {number} rate - Speech rate
 * @returns {number} Display time in milliseconds
 */
export function calculateAnnotationDisplayTime(content, rate = 1.0) {
  if (!content || !content.trim()) {
    return 750; // Base time for empty content
  }
  
  // Strip HTML tags for accurate calculation
  const textContent = content.replace(/<[^>]*>/g, '').trim();
  
  // Use TTS timing estimation
  return estimateReadMs(textContent, rate);
}

/**
 * Parse annotation text for TTS
 * Removes HTML tags and cleans up for speech
 * @param {string} content - Raw annotation content
 * @returns {string} Clean text for TTS
 */
export function cleanTextForTTS(content) {
  if (!content) return '';
  
  // Remove HTML tags
  let cleanText = content.replace(/<[^>]*>/g, '');
  
  // Replace HTML entities
  cleanText = cleanText
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
  
  // Clean up excessive whitespace
  cleanText = cleanText.replace(/\s+/g, ' ').trim();
  
  return cleanText;
}

/**
 * Split long text into chunks for better TTS handling
 * @param {string} text - Text to split
 * @param {number} maxLength - Maximum chunk length (default: 200 chars)
 * @returns {Array<string>} Array of text chunks
 */
export function splitTextForTTS(text, maxLength = 200) {
  if (!text || text.length <= maxLength) {
    return [text];
  }
  
  const chunks = [];
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  
  let currentChunk = '';
  
  for (const sentence of sentences) {
    if (currentChunk.length + sentence.length <= maxLength) {
      currentChunk += sentence;
    } else {
      if (currentChunk) {
        chunks.push(currentChunk.trim());
      }
      currentChunk = sentence;
    }
  }
  
  if (currentChunk) {
    chunks.push(currentChunk.trim());
  }
  
  return chunks;
}

/**
 * Format move notation for speech
 * Converts chess notation to more speakable format
 * @param {string} notation - Chess move notation
 * @returns {string} Speech-friendly notation
 */
export function formatMoveForSpeech(notation) {
  if (!notation) return '';
  
  // Replace chess symbols with words
  let speech = notation
    .replace(/K/g, 'King ')
    .replace(/Q/g, 'Queen ')
    .replace(/R/g, 'Rook ')
    .replace(/B/g, 'Bishop ')
    .replace(/N/g, 'Knight ')
    .replace(/x/g, ' takes ')
    .replace(/\+/g, ' check')
    .replace(/#/g, ' checkmate')
    .replace(/O-O-O/g, 'castle queenside')
    .replace(/O-O/g, 'castle kingside')
    .replace(/=/g, ' promotes to ');
  
  // Clean up spacing
  speech = speech.replace(/\s+/g, ' ').trim();
  
  return speech;
}